import type {
    DraftFormSubmissionRequest,
    SilentDraftFormSubmissionRequest,
} from '@common/types/workflows';
import { useEffect, useRef } from 'react';
import { clientSideSilentDraftIntakeFormSave } from '@/services/ClientSideTemplateFormService';
import { DRAFT_AUTOSAVE_INTERVAL_MS } from '../timingConfig';

function getAutosavePayloadHash(payload: SilentDraftFormSubmissionRequest): string {
    return JSON.stringify(payload);
}

interface UseSilentDraftAutosaveOptions {
    enabled: boolean;
    shouldUpsertDraft: boolean;
    initialDraftFormSubmissionId?: string | null;
    buildPayload: (draftFormSubmissionId?: string) => DraftFormSubmissionRequest | null | undefined;
    onDraftFormSubmissionIdChange?: (draftFormSubmissionId: string) => void;
    isBlocked?: boolean;
}

export function useSilentDraftAutosave({
    enabled,
    shouldUpsertDraft,
    initialDraftFormSubmissionId,
    buildPayload,
    onDraftFormSubmissionIdChange,
    isBlocked = false,
}: UseSilentDraftAutosaveOptions) {
    const draftFormSubmissionIdRef = useRef(initialDraftFormSubmissionId || undefined);
    const buildPayloadRef = useRef(buildPayload);
    const shouldUpsertDraftRef = useRef(shouldUpsertDraft);
    const onDraftFormSubmissionIdChangeRef = useRef(onDraftFormSubmissionIdChange);
    const isBlockedRef = useRef(isBlocked);
    const isSavingRef = useRef(false);
    const lastSavedPayloadHashRef = useRef<string | null>(null);

    useEffect(() => {
        draftFormSubmissionIdRef.current = initialDraftFormSubmissionId || undefined;
    }, [initialDraftFormSubmissionId]);

    useEffect(() => {
        buildPayloadRef.current = buildPayload;
    }, [buildPayload]);

    useEffect(() => {
        shouldUpsertDraftRef.current = shouldUpsertDraft;
    }, [shouldUpsertDraft]);

    useEffect(() => {
        onDraftFormSubmissionIdChangeRef.current = onDraftFormSubmissionIdChange;
    }, [onDraftFormSubmissionIdChange]);

    useEffect(() => {
        isBlockedRef.current = isBlocked;
    }, [isBlocked]);

    useEffect(() => {
        if (!enabled) {
            lastSavedPayloadHashRef.current = null;
            return;
        }

        const autosave = async () => {
            if (isBlockedRef.current || isSavingRef.current) return;

            try {
                const payload = buildPayloadRef.current(draftFormSubmissionIdRef.current);
                if (!payload) return;

                const requestPayload: SilentDraftFormSubmissionRequest = {
                    ...payload,
                    shouldUpsertDraft: shouldUpsertDraftRef.current,
                };
                const payloadHash = getAutosavePayloadHash(requestPayload);
                if (payloadHash === lastSavedPayloadHashRef.current) return;

                isSavingRef.current = true;
                const response = await clientSideSilentDraftIntakeFormSave(requestPayload);
                lastSavedPayloadHashRef.current = payloadHash;
                const draftFormSubmissionId = response.data.DraftFormSubmissionId;

                if (draftFormSubmissionId) {
                    draftFormSubmissionIdRef.current = draftFormSubmissionId;
                    onDraftFormSubmissionIdChangeRef.current?.(draftFormSubmissionId);
                }
            } catch {
                // Silent autosave is best effort.
            } finally {
                isSavingRef.current = false;
            }
        };

        const interval = setInterval(() => {
            void autosave();
        }, DRAFT_AUTOSAVE_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [enabled]);
}
